import React, { useMemo, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  Plate,
  PlateProvider,
  createPlugins,
} from '@udecode/plate';
import clsx from 'clsx';

import { useStickyElement } from 'hooks/useStickyElement';
import { plugins } from './contexts/fullEditor';
import { Toolbar } from './components/toolbar/Toolbar';
import { createDocumentSettingsPlugin } from './plugins/document-settings/createDocumentSettingsPlugin';
import { createColorPickerSettingsPlugin } from './plugins/color-picker-settings/createColorPickerSettingsPlugin';
import ErrorModal from './components/popup/Error/ErrorModal';

import './plate.scss';

const editableProps = {
  placeholder: 'Type...',
  style: { minHeight: '100px' },
};

const PlateEditor = ({
  rootElement, // React dnd will error with setting up multiple html5 backends, so root element needs to be provided.
  initialValue,
  onChange,
  stickyProps,
  toolbarProps,
  documentSettings,
}) => {
  const editorRef = useRef(null);
  const { offset } = stickyProps || {};
  const { colorPickerProps } = toolbarProps || {};
  const { elementRef, isSticky } = stickyProps ? useStickyElement(false, offset) : {};

  const addtPlugins = useMemo(
    () => createPlugins([
      createColorPickerSettingsPlugin({
        options: colorPickerProps,
      }),
      createDocumentSettingsPlugin({
        options: documentSettings,
      }),
    ]),
    [colorPickerProps, documentSettings],
  );

  // We don't want to call handle change when user changes the selection by clicking into the editor
  // or other related events.
  const handleChange = (value => {
    if (editorRef.current) {
      const isAstChange = editorRef.current.operations.some(op => op.type !== 'set_selection');
      if (!isAstChange) {
        return;
      }
    }
    onChange(value);
  });

  return (
    <div className="ap-slate-editor">
      <DndProvider
        backend={HTML5Backend}
        options={{ rootElement }}
      >
        <PlateProvider
          editorRef={editorRef}
          initialValue={initialValue}
          normalizeInitialValue
          plugins={[...plugins, ...addtPlugins]}
          onChange={handleChange}
        >
          <div
            ref={elementRef}
            className={clsx('plate-toolbar__wrapper', isSticky && 'has-detached-element')}
          >
            <Toolbar
              isSticky={isSticky}
              toolbarProps={toolbarProps}
            />
          </div>
          <Plate
            editableProps={editableProps}
          />
          <ErrorModal/>
        </PlateProvider>
      </DndProvider>
    </div>
  );
};

export default PlateEditor;
